/**
 * Regulatory Sources Admin - NAVI Teachable Interface
 * Manage regulatory sources, configure scrapers, and train NAVI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, GearSix, Play, Eye, Trash, Check, X, 
  Robot, Globe, Buildings, FileText, Clock, Warning,
  MagnifyingGlass, Funnel, CaretDown, CaretUp, Code,
  Lightning, Database, ChartBar, Spinner
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// ============================================================
// TYPES
// ============================================================

const SOURCE_TYPES = [
  { id: 'gazette', name: 'Government Gazette', icon: FileText },
  { id: 'regulator', name: 'Regulator', icon: Buildings },
  { id: 'parliament', name: 'Parliament', icon: Globe },
  { id: 'international', name: 'International', icon: Globe },
];

const DOCUMENT_TYPES = [
  { id: 'primary_act', name: 'Primary Act' },
  { id: 'amendment_act', name: 'Amendment Act' },
  { id: 'regulations', name: 'Regulations' },
  { id: 'rules', name: 'Rules' },
  { id: 'notice', name: 'Notice' },
  { id: 'directive', name: 'Directive' },
  { id: 'guidance', name: 'Guidance' },
  { id: 'circular', name: 'Circular' },
  { id: 'standard', name: 'Standard' },
  { id: 'draft_bill', name: 'Draft Bill' },
  { id: 'consultation', name: 'Consultation' },
  { id: 'enforcement', name: 'Enforcement' },
  { id: 'exemption', name: 'Exemption' },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

const RegulatorySourcesAdmin = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('sources'); // sources, documents, training, stats
  const [sources, setSources] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAddSource, setShowAddSource] = useState(false);
  const [showTestScraper, setShowTestScraper] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  
  // Load data
  useEffect(() => {
    loadData();
  }, [token]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [sourcesRes, templatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/regulatory-sources/sources`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/regulatory-sources/sources/templates`),
        axios.get(`${API}/regulatory-sources/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      
      setSources(sourcesRes.data.sources || []);
      setTemplates(templatesRes.data.templates || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load regulatory sources');
    } finally {
      setLoading(false);
    }
  };
  
  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${API}/regulatory-sources/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
  }, [activeTab]);
  
  return (
    <div className="min-h-screen bg-background" data-testid="regulatory-sources-admin">
      {/* Header */}
      <div className="bg-background-surface border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-background rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-text-secondary" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                  <Robot size={24} className="text-primary" />
                  NAVI Source Intelligence
                </h1>
                <p className="text-sm text-text-secondary">
                  Teach NAVI where to find regulatory documents
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowAddSource(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus size={18} />
              Add Source
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {[
              { id: 'sources', label: 'Sources', icon: Globe },
              { id: 'documents', label: 'Documents', icon: FileText },
              { id: 'training', label: 'Training', icon: Robot },
              { id: 'stats', label: 'Statistics', icon: ChartBar },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-secondary hover:bg-background'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-primary" />
          </div>
        ) : (
          <>
            {activeTab === 'sources' && (
              <SourcesTab 
                sources={sources} 
                templates={templates}
                token={token}
                onRefresh={loadData}
                onTestScraper={(source) => {
                  setSelectedSource(source);
                  setShowTestScraper(true);
                }}
              />
            )}
            {activeTab === 'documents' && (
              <DocumentsTab 
                documents={documents} 
                token={token}
                onRefresh={loadDocuments}
              />
            )}
            {activeTab === 'training' && (
              <TrainingTab token={token} />
            )}
            {activeTab === 'stats' && (
              <StatsTab stats={stats} />
            )}
          </>
        )}
      </div>
      
      {/* Add Source Modal */}
      {showAddSource && (
        <AddSourceModal
          templates={templates}
          token={token}
          onClose={() => setShowAddSource(false)}
          onSuccess={() => {
            setShowAddSource(false);
            loadData();
          }}
        />
      )}
      
      {/* Test Scraper Modal */}
      {showTestScraper && selectedSource && (
        <TestScraperModal
          source={selectedSource}
          token={token}
          onClose={() => {
            setShowTestScraper(false);
            setSelectedSource(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================
// SOURCES TAB
// ============================================================

const SourcesTab = ({ sources, templates, token, onRefresh, onTestScraper }) => {
  const [crawling, setCrawling] = useState({});
  
  const handleTriggerCrawl = async (sourceId) => {
    setCrawling(prev => ({ ...prev, [sourceId]: true }));
    try {
      await axios.post(
        `${API}/regulatory-sources/sources/${sourceId}/crawl`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Crawl started in background');
    } catch (error) {
      toast.error('Failed to start crawl');
    } finally {
      setCrawling(prev => ({ ...prev, [sourceId]: false }));
    }
  };
  
  const handleToggleActive = async (source) => {
    try {
      await axios.patch(
        `${API}/regulatory-sources/sources/${source.id}`,
        { is_active: !source.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRefresh();
      toast.success(source.is_active ? 'Source deactivated' : 'Source activated');
    } catch (error) {
      toast.error('Failed to update source');
    }
  };
  
  const handleDeleteSource = async (sourceId) => {
    if (!confirm('Are you sure you want to delete this source?')) return;
    
    try {
      await axios.delete(
        `${API}/regulatory-sources/sources/${sourceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRefresh();
      toast.success('Source deleted');
    } catch (error) {
      toast.error('Failed to delete source');
    }
  };
  
  const configuredShortNames = sources.map(s => s.short_name);
  const availableTemplates = templates.filter(t => !configuredShortNames.includes(t.short_name));
  
  return (
    <div className="space-y-6">
      {/* Quick Add from Templates */}
      {availableTemplates.length > 0 && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h3 className="text-sm font-medium text-text-primary mb-3">
            Quick Add from Templates
          </h3>
          <div className="flex flex-wrap gap-2">
            {availableTemplates.map(template => (
              <QuickAddTemplate 
                key={template.short_name} 
                template={template} 
                token={token}
                onSuccess={onRefresh}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Sources List */}
      <div className="grid gap-4">
        {sources.length === 0 ? (
          <div className="text-center py-12 text-text-secondary">
            <Globe size={48} className="mx-auto mb-4 opacity-50" />
            <p>No sources configured yet.</p>
            <p className="text-sm">Add a source from templates above or create a custom one.</p>
          </div>
        ) : (
          sources.map(source => (
            <SourceCard
              key={source.id}
              source={source}
              crawling={crawling[source.id]}
              onTest={() => onTestScraper(source)}
              onCrawl={() => handleTriggerCrawl(source.id)}
              onToggle={() => handleToggleActive(source)}
              onDelete={() => handleDeleteSource(source.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

const QuickAddTemplate = ({ template, token, onSuccess }) => {
  const [adding, setAdding] = useState(false);
  
  const handleAdd = async () => {
    setAdding(true);
    try {
      await axios.post(
        `${API}/regulatory-sources/sources/from-template/${template.short_name}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${template.short_name} added`);
      onSuccess();
    } catch (error) {
      toast.error(`Failed to add ${template.short_name}`);
    } finally {
      setAdding(false);
    }
  };
  
  return (
    <button
      onClick={handleAdd}
      disabled={adding}
      className="flex items-center gap-2 px-3 py-1.5 bg-background-surface border border-border rounded-lg text-sm text-text-secondary hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
    >
      <Plus size={14} />
      {template.short_name}
    </button>
  );
};

const SourceCard = ({ source, crawling, onTest, onCrawl, onToggle, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusColor = {
    success: 'text-emerald-400',
    partial: 'text-amber-400',
    failed: 'text-red-400',
    pending: 'text-text-tertiary',
  };
  
  return (
    <div className="bg-background-surface border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${source.is_active ? 'bg-emerald-400' : 'bg-text-tertiary'}`} />
          <div>
            <h3 className="font-medium text-text-primary">{source.name}</h3>
            <p className="text-sm text-text-secondary">{source.base_url}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {source.last_crawl_status && (
            <span className={`text-xs ${statusColor[source.last_crawl_status] || 'text-text-tertiary'}`}>
              {source.last_crawl_documents_found} docs found
            </span>
          )}
          
          <button
            onClick={onTest}
            className="p-2 text-text-secondary hover:text-primary transition-colors"
            title="Test Scraper"
          >
            <Code size={18} />
          </button>
          
          <button
            onClick={onCrawl}
            disabled={crawling}
            className="p-2 text-text-secondary hover:text-primary transition-colors disabled:opacity-50"
            title="Run Crawl"
          >
            {crawling ? <Spinner size={18} className="animate-spin" /> : <Play size={18} />}
          </button>
          
          <button
            onClick={onToggle}
            className="p-2 text-text-secondary hover:text-primary transition-colors"
            title={source.is_active ? 'Deactivate' : 'Activate'}
          >
            {source.is_active ? <Check size={18} /> : <X size={18} />}
          </button>
          
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-text-secondary hover:text-primary transition-colors"
          >
            {expanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
          </button>
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-text-tertiary">Type:</span>
              <span className="ml-2 text-text-primary">{source.source_type}</span>
            </div>
            <div>
              <span className="text-text-tertiary">Frequency:</span>
              <span className="ml-2 text-text-primary">Every {source.crawl_frequency_hours}h</span>
            </div>
            <div>
              <span className="text-text-tertiary">Rules:</span>
              <span className="ml-2 text-text-primary">{source.scraping_rules?.length || 0}</span>
            </div>
            <div>
              <span className="text-text-tertiary">Last Crawl:</span>
              <span className="ml-2 text-text-primary">
                {source.last_crawl_at ? new Date(source.last_crawl_at).toLocaleDateString() : 'Never'}
              </span>
            </div>
          </div>
          
          {source.scraping_rules?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-text-secondary mb-2">Scraping Rules</h4>
              <div className="space-y-2">
                {source.scraping_rules.map((rule, idx) => (
                  <div key={rule.id || idx} className="p-3 bg-background rounded-lg text-sm">
                    <div className="text-text-primary truncate">{rule.start_url}</div>
                    <div className="text-xs text-text-tertiary mt-1">
                      Container: {rule.document_container_selector || 'Not set'} | 
                      Selectors: {rule.selectors?.length || 0}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={onDelete}
              className="text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              Delete Source
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// DOCUMENTS TAB
// ============================================================

const DocumentsTab = ({ documents, token, onRefresh }) => {
  const [classifying, setClassifying] = useState({});
  
  const handleClassify = async (docId) => {
    setClassifying(prev => ({ ...prev, [docId]: true }));
    try {
      const res = await axios.post(
        `${API}/regulatory-sources/documents/${docId}/classify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success('Document classified');
        onRefresh();
      } else {
        toast.error('Classification failed');
      }
    } catch (error) {
      toast.error('Failed to classify document');
    } finally {
      setClassifying(prev => ({ ...prev, [docId]: false }));
    }
  };
  
  return (
    <div className="space-y-4">
      {documents.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">
          <FileText size={48} className="mx-auto mb-4 opacity-50" />
          <p>No documents extracted yet.</p>
          <p className="text-sm">Run a crawl on a source to extract documents.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {documents.map(doc => (
            <div key={doc.id} className="p-4 bg-background-surface border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-text-primary">{doc.title}</h3>
                  <p className="text-sm text-text-secondary mt-1">
                    {doc.source_name} • {doc.publication_date || 'Date unknown'}
                  </p>
                  
                  {doc.navi_classification && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                        {doc.navi_classification}
                      </span>
                      <span className="text-xs text-text-tertiary">
                        {Math.round((doc.navi_confidence || 0) * 100)}% confidence
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {!doc.navi_classification && (
                    <button
                      onClick={() => handleClassify(doc.id)}
                      disabled={classifying[doc.id]}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {classifying[doc.id] ? (
                        <Spinner size={14} className="animate-spin" />
                      ) : (
                        <Robot size={14} />
                      )}
                      Classify
                    </button>
                  )}
                  
                  {doc.pdf_url && (
                    <a
                      href={doc.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-text-secondary hover:text-primary transition-colors"
                    >
                      <Eye size={18} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// TRAINING TAB
// ============================================================

const TrainingTab = ({ token }) => {
  return (
    <div className="space-y-6">
      <div className="p-6 bg-background-surface border border-border rounded-lg text-center">
        <Robot size={48} className="mx-auto mb-4 text-primary" />
        <h2 className="text-lg font-semibold text-text-primary">Train NAVI</h2>
        <p className="text-sm text-text-secondary mt-2 max-w-md mx-auto">
          Review extracted documents and correct their classifications to improve NAVI's accuracy.
          The more training data you provide, the better NAVI becomes at identifying document types.
        </p>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-background rounded-lg">
            <div className="text-2xl font-bold text-primary">0</div>
            <div className="text-sm text-text-secondary">Training Samples</div>
          </div>
          <div className="p-4 bg-background rounded-lg">
            <div className="text-2xl font-bold text-emerald-400">0</div>
            <div className="text-sm text-text-secondary">Documents Reviewed</div>
          </div>
          <div className="p-4 bg-background rounded-lg">
            <div className="text-2xl font-bold text-amber-400">0</div>
            <div className="text-sm text-text-secondary">Pending Review</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STATS TAB
// ============================================================

const StatsTab = ({ stats }) => {
  if (!stats) return null;
  
  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <div className="text-2xl font-bold text-primary">{stats.sources?.total || 0}</div>
          <div className="text-sm text-text-secondary">Total Sources</div>
        </div>
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <div className="text-2xl font-bold text-emerald-400">{stats.sources?.active || 0}</div>
          <div className="text-sm text-text-secondary">Active Sources</div>
        </div>
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <div className="text-2xl font-bold text-text-primary">{stats.documents?.total || 0}</div>
          <div className="text-sm text-text-secondary">Documents Extracted</div>
        </div>
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <div className="text-2xl font-bold text-amber-400">{stats.documents?.unreviewed || 0}</div>
          <div className="text-sm text-text-secondary">Pending Review</div>
        </div>
      </div>
      
      {/* Documents by Type */}
      {stats.documents?.by_type && Object.keys(stats.documents.by_type).length > 0 && (
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <h3 className="font-medium text-text-primary mb-4">Documents by Type</h3>
          <div className="space-y-2">
            {Object.entries(stats.documents.by_type).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary capitalize">{type.replace('_', ' ')}</span>
                <span className="text-sm font-medium text-text-primary">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Recent Crawls */}
      {stats.recent_crawls?.length > 0 && (
        <div className="p-4 bg-background-surface border border-border rounded-lg">
          <h3 className="font-medium text-text-primary mb-4">Recent Crawls</h3>
          <div className="space-y-2">
            {stats.recent_crawls.map(crawl => (
              <div key={crawl.id} className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">{crawl.source_name}</span>
                <div className="flex items-center gap-4">
                  <span className="text-text-tertiary">
                    {crawl.new_documents} new / {crawl.documents_found} total
                  </span>
                  <span className={`capitalize ${
                    crawl.status === 'success' ? 'text-emerald-400' :
                    crawl.status === 'failed' ? 'text-red-400' : 'text-amber-400'
                  }`}>
                    {crawl.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MODALS
// ============================================================

const AddSourceModal = ({ templates, token, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [sourceType, setSourceType] = useState('regulator');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    if (!name || !shortName || !baseUrl) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/regulatory-sources/sources`,
        {
          name,
          short_name: shortName,
          base_url: baseUrl,
          source_type: sourceType,
          crawler_type: 'playwright',
          is_active: true,
          crawl_frequency_hours: 168,
          priority: 3,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Source created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create source');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background-surface border border-border rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-text-primary">Add Regulatory Source</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-text-secondary">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Financial Sector Conduct Authority"
              className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-text-secondary">Short Name *</label>
            <input
              type="text"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              placeholder="e.g., FSCA"
              className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-text-secondary">Base URL *</label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://www.fsca.co.za"
              className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-text-secondary">Source Type</label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="w-full mt-1 px-4 py-2 bg-background border border-border rounded-lg text-text-primary"
            >
              {SOURCE_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="p-4 border-t border-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Source'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TestScraperModal = ({ source, token, onClose }) => {
  const [testUrl, setTestUrl] = useState('');
  const [selector, setSelector] = useState('');
  const [attribute, setAttribute] = useState('');
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);
  const [ruleTestResults, setRuleTestResults] = useState(null);
  
  // Test a single selector
  const handleTestSelector = async () => {
    if (!testUrl || !selector) {
      toast.error('Enter URL and selector');
      return;
    }
    
    setTesting(true);
    setResults(null);
    
    try {
      const res = await axios.post(
        `${API}/regulatory-sources/test-selector`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { url: testUrl, selector, attribute: attribute || undefined }
        }
      );
      setResults(res.data);
    } catch (error) {
      toast.error('Test failed');
      setResults({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };
  
  // Test full scraping rule
  const handleTestRule = async () => {
    setTesting(true);
    setRuleTestResults(null);
    
    try {
      const res = await axios.post(
        `${API}/regulatory-sources/sources/${source.id}/test-scraper`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRuleTestResults(res.data);
    } catch (error) {
      toast.error('Test failed');
      setRuleTestResults({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background-surface border border-border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-text-primary">Test Scraper - {source.short_name}</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Selector Tester */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-text-primary">Test Individual Selector</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-text-tertiary">URL</label>
                <input
                  type="url"
                  value={testUrl}
                  onChange={(e) => setTestUrl(e.target.value)}
                  placeholder={source.base_url}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary"
                />
              </div>
              <div>
                <label className="text-xs text-text-tertiary">CSS Selector</label>
                <input
                  type="text"
                  value={selector}
                  onChange={(e) => setSelector(e.target.value)}
                  placeholder="e.g., .document-title"
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-text-tertiary">Attribute (optional)</label>
                <input
                  type="text"
                  value={attribute}
                  onChange={(e) => setAttribute(e.target.value)}
                  placeholder="e.g., href"
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary font-mono"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleTestSelector}
                  disabled={testing}
                  className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {testing ? 'Testing...' : 'Test Selector'}
                </button>
              </div>
            </div>
            
            {results && (
              <div className={`p-4 rounded-lg ${results.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {results.success ? (
                    <Check size={16} className="text-emerald-400" />
                  ) : (
                    <X size={16} className="text-red-400" />
                  )}
                  <span className="text-sm font-medium">
                    {results.success ? `Found ${results.matches} matches` : 'Test failed'}
                  </span>
                </div>
                
                {results.results?.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {results.results.slice(0, 10).map((r, i) => (
                      <div key={i} className="text-xs text-text-secondary bg-background p-2 rounded">
                        {r.value}
                      </div>
                    ))}
                  </div>
                )}
                
                {results.error && (
                  <p className="text-xs text-red-400 mt-2">{results.error}</p>
                )}
              </div>
            )}
          </div>
          
          {/* Full Rule Test */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-text-primary">Test Full Scraping Rule</h3>
              <button
                onClick={handleTestRule}
                disabled={testing || !source.scraping_rules?.length}
                className="px-4 py-2 bg-background border border-border rounded-lg text-sm text-text-secondary hover:text-primary hover:border-primary disabled:opacity-50 transition-colors"
              >
                <Lightning size={16} className="inline mr-1" />
                Run Full Test
              </button>
            </div>
            
            {ruleTestResults && (
              <div className={`p-4 rounded-lg ${ruleTestResults.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {ruleTestResults.success ? (
                    <Check size={16} className="text-emerald-400" />
                  ) : (
                    <X size={16} className="text-red-400" />
                  )}
                  <span className="text-sm font-medium">
                    {ruleTestResults.success 
                      ? `Extracted ${ruleTestResults.documents_count} documents` 
                      : 'Test failed'}
                  </span>
                </div>
                
                {/* Logs */}
                {ruleTestResults.logs?.length > 0 && (
                  <div className="mt-2 p-2 bg-background rounded text-xs font-mono text-text-tertiary max-h-32 overflow-y-auto">
                    {ruleTestResults.logs.map((log, i) => (
                      <div key={i}>{log}</div>
                    ))}
                  </div>
                )}
                
                {/* Documents */}
                {ruleTestResults.documents?.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-medium text-text-secondary mb-2">Extracted Documents:</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {ruleTestResults.documents.map((doc, i) => (
                        <div key={i} className="p-2 bg-background rounded text-sm">
                          <div className="font-medium text-text-primary">{doc.title || 'No title'}</div>
                          {doc.date && <div className="text-xs text-text-tertiary">Date: {doc.date}</div>}
                          {doc.pdf_url && <div className="text-xs text-primary truncate">PDF: {doc.pdf_url}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {ruleTestResults.error && (
                  <p className="text-xs text-red-400 mt-2">{ruleTestResults.error}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegulatorySourcesAdmin;
